// ═══════════════════════════════════════════════════════════════
// SHELTER MANAGEMENT — Medical Log
// Vitals, medications, incidents, notes, referrals per survivor
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, Switch, Drawer, Divider,
} from '@mantine/core';
import {
  IconSearch, IconPlus, IconPencil, IconRefresh, IconAlertTriangle,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { MedicalLogEntry } from '../../types/shelter';

const ENTRY_TYPE_OPTIONS = [
  { value: 'vitals', label: 'Vitals' },
  { value: 'medication', label: 'Medication' },
  { value: 'incident', label: 'Incident' },
  { value: 'note', label: 'Note' },
  { value: 'referral', label: 'Referral' },
];

const ENTRY_TYPE_COLORS: Record<string, string> = {
  vitals: 'blue', medication: 'grape', incident: 'red', note: 'gray', referral: 'teal',
};

const EMPTY: Partial<MedicalLogEntry> = {
  survivorId: '', entryType: 'note', description: '', medicationName: '',
  dosage: '', administeredBy: '', followUpNeeded: false, followUpDate: '', notes: '',
};

export default function Medical() {
  const navigate = useNavigate();
  const { activeIncident, user } = useAuthStore();
  const { canEdit } = useModulePermission('shelter');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    medicalLog, survivors, shelters,
    loadingMedical,
    loadMedicalLog, loadSurvivors, loadShelters,
    createMedicalEntry, updateMedicalEntry,
  } = useShelterStore();

  const [search, setSearch] = useState('');

  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const [shelterFilter, setShelterFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [followUpFilter, setFollowUpFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MedicalLogEntry | null>(null);
  const [detailTarget, setDetailTarget] = useState<MedicalLogEntry | null>(null);
  const [form, setForm] = useState<Partial<MedicalLogEntry>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadMedicalLog(); loadSurvivors(); loadShelters(); }, [loadMedicalLog, loadSurvivors, loadShelters]);

  // Store already server-side filters by active incident; use directly
  const iEntries = incidentId ? medicalLog : [];
  const iSurvivors = incidentId ? survivors : [];
  const iShelters = incidentId ? shelters : [];

  // Lookup maps
  const survivorMap = useMemo(() => {
    const m = new Map<string, string>();
    iSurvivors.forEach(s => m.set(String(s.Rid), `${s.firstName} ${s.lastName}`));
    return m;
  }, [iSurvivors]);

  const survivorOptions = useMemo(() =>
    iSurvivors.map(s => ({ value: String(s.Rid), label: `${s.firstName} ${s.lastName}` })),
    [iSurvivors],
  );

  const shelterMap = useMemo(() => {
    const m = new Map<string, string>();
    iShelters.forEach(s => m.set(String(s.Rid), s.name));
    return m;
  }, [iShelters]);

  const shelterOptions = useMemo(() =>
    [{ value: 'all', label: 'All Shelters' }, ...iShelters.map(s => ({ value: String(s.Rid), label: s.name }))],
    [iShelters],
  );

  const filtered = useMemo(() => {
    let list = iEntries;
    if (shelterFilter && shelterFilter !== 'all') list = list.filter(m => m.shelterId === shelterFilter);
    if (typeFilter && typeFilter !== 'all') list = list.filter(m => m.entryType === typeFilter);
    if (followUpFilter === 'yes') list = list.filter(m => m.followUpNeeded);
    if (followUpFilter === 'no') list = list.filter(m => !m.followUpNeeded);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        m.description.toLowerCase().includes(q) ||
        m.medicationName.toLowerCase().includes(q) ||
        m.administeredBy.toLowerCase().includes(q) ||
        (survivorMap.get(m.survivorId) ?? '').toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [iEntries, shelterFilter, typeFilter, followUpFilter, search, survivorMap, sortField, sortDir]);

  const setField = (key: keyof MedicalLogEntry, value: unknown) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY, administeredBy: user?.fullName ?? user?.email ?? '' });
    setModalOpen(true);
  };

  const openEdit = (m: MedicalLogEntry) => { setEditTarget(m); setForm({ ...m }); setModalOpen(true); };

  const handleSave = async () => {
    if (!form.survivorId) {
      notifications.show({ message: 'Survivor is required', color: 'yellow' });
      return;
    }
    if (!form.description?.trim()) {
      notifications.show({ message: 'Description is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      // Find survivor's shelter
      const survivor = iSurvivors.find(s => String(s.Rid) === form.survivorId);
      const shelterId = survivor?.shelterId ?? '';

      const payload: Partial<MedicalLogEntry> = {
        ...form,
        incidentId,
        shelterId,
        timestamp: editTarget ? form.timestamp : new Date().toISOString(),
        followUpNeeded: form.followUpNeeded ?? false,
      };

      if (editTarget) {
        await updateMedicalEntry(editTarget.Rid, payload);
        notifications.show({ message: 'Medical entry updated', color: 'green' });
      } else {
        await createMedicalEntry(payload);
        notifications.show({ message: 'Medical entry created', color: 'green' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Medical Log</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingMedical} onClick={() => loadMedicalLog()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Entry</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm" wrap="wrap">
        <TextInput placeholder="Search description, medication, person..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.currentTarget.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={shelterOptions}
          value={shelterFilter ?? 'all'} onChange={v => setShelterFilter(v)} size="xs" w={180}
          placeholder="Filter by shelter" />
        <Select data={[{ value: 'all', label: 'All Types' }, ...ENTRY_TYPE_OPTIONS]}
          value={typeFilter ?? 'all'} onChange={v => setTypeFilter(v)} size="xs" w={150} />
        <Select data={[{ value: 'all', label: 'All' }, { value: 'yes', label: 'Needs Follow-Up' }, { value: 'no', label: 'No Follow-Up' }]}
          value={followUpFilter ?? 'all'} onChange={v => setFollowUpFilter(v)} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} entr{filtered.length !== 1 ? 'ies' : 'y'}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Survivor</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('entryType')}>Type{sortIcon('entryType')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Description</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Medication</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Administered By</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('timestamp')}>Timestamp{sortIcon('timestamp')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'center' }}>Follow-Up</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingMedical ? 'Loading...' : 'No medical log entries found'}
                </td></tr>
              )}
              {filtered.map(m => {
                const needsFollowUp = m.followUpNeeded && !m.followUpDate;
                const overdue = m.followUpNeeded && m.followUpDate && new Date(m.followUpDate) < new Date();
                return (
                  <tr key={m.id}
                    onClick={() => setDetailTarget(m)}
                    style={{
                      cursor: 'pointer',
                      borderBottom: '1px solid var(--mantine-color-gray-2)',
                      backgroundColor: (needsFollowUp || overdue) ? 'var(--mantine-color-yellow-0)' : undefined,
                    }}>
                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                      {(survivorMap.get(m.survivorId) ?? m.survivorId) || '-'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Badge size="sm" color={ENTRY_TYPE_COLORS[m.entryType] ?? 'gray'}>
                        {m.entryType}
                      </Badge>
                    </td>
                    <td style={{ padding: '8px 12px', maxWidth: 250 }}>
                      <Text size="sm" lineClamp={2}>{m.description || '-'}</Text>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {m.medicationName ? (
                        <Text size="sm">{m.medicationName}{m.dosage ? ` (${m.dosage})` : ''}</Text>
                      ) : '-'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>{m.administeredBy || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <Text size="sm" c="dimmed">
                        {m.timestamp ? new Date(m.timestamp).toLocaleString() : '-'}
                      </Text>
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      {m.followUpNeeded ? (
                        <Group gap={4} justify="center">
                          <IconAlertTriangle size={14} color={overdue ? '#dc2626' : '#d97706'} />
                          <Badge size="xs" color={overdue ? 'red' : 'yellow'}>
                            {overdue ? 'Overdue' : m.followUpDate ? new Date(m.followUpDate).toLocaleDateString() : 'Needed'}
                          </Badge>
                        </Group>
                      ) : (
                        <Text size="xs" c="dimmed">-</Text>
                      )}
                    </td>
                    {canEdit && (
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <ActionIcon size="sm" variant="subtle" onClick={e => { e.stopPropagation(); openEdit(m); }}>
                        <IconPencil size={14} />
                      </ActionIcon>
                    </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Detail Drawer */}
      <Drawer opened={!!detailTarget} onClose={() => setDetailTarget(null)}
        position="right" size="md" title="Medical Entry Details">
        {detailTarget && (
          <Stack>
            <Group>
              <Badge size="lg" color={ENTRY_TYPE_COLORS[detailTarget.entryType] ?? 'gray'}>
                {detailTarget.entryType}
              </Badge>
              <Text size="sm" c="dimmed">
                {detailTarget.timestamp ? new Date(detailTarget.timestamp).toLocaleString() : '-'}
              </Text>
            </Group>

            <Divider />

            <div>
              <Text size="xs" c="dimmed">Survivor</Text>
              <Group gap="xs">
                <Text size="sm" fw={500}>{survivorMap.get(detailTarget.survivorId) ?? detailTarget.survivorId ?? '-'}</Text>
                {detailTarget.survivorId && (
                  <Button size="compact-xs" variant="subtle"
                    onClick={() => { setDetailTarget(null); navigate('/shelter/survivor/' + detailTarget.survivorId); }}>
                    View Survivor
                  </Button>
                )}
              </Group>
            </div>

            <div>
              <Text size="xs" c="dimmed">Shelter</Text>
              <Text size="sm">{shelterMap.get(detailTarget.shelterId) ?? detailTarget.shelterId ?? '-'}</Text>
            </div>

            <div>
              <Text size="xs" c="dimmed">Description</Text>
              <Text size="sm">{detailTarget.description || '-'}</Text>
            </div>

            {detailTarget.medicationName && (
              <>
                <Divider label="Medication" labelPosition="left" />
                <SimpleGrid cols={3}>
                  <div>
                    <Text size="xs" c="dimmed">Medication</Text>
                    <Text size="sm">{detailTarget.medicationName}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Dosage</Text>
                    <Text size="sm">{detailTarget.dosage || '-'}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Administered By</Text>
                    <Text size="sm">{detailTarget.administeredBy || '-'}</Text>
                  </div>
                </SimpleGrid>
              </>
            )}

            <Divider />

            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed">Follow-Up Needed</Text>
                <Badge size="sm" color={detailTarget.followUpNeeded ? 'yellow' : 'gray'}>
                  {detailTarget.followUpNeeded ? 'Yes' : 'No'}
                </Badge>
              </div>
              {detailTarget.followUpNeeded && detailTarget.followUpDate && (
                <div>
                  <Text size="xs" c="dimmed">Follow-Up Date</Text>
                  <Text size="sm">{new Date(detailTarget.followUpDate).toLocaleDateString()}</Text>
                </div>
              )}
            </SimpleGrid>

            <div>
              <Text size="xs" c="dimmed">Notes</Text>
              <Text size="sm">{detailTarget.notes || '-'}</Text>
            </div>

            <Divider />

            {canEdit && (
            <Group justify="flex-end">
              <Button size="xs" variant="light" leftSection={<IconPencil size={14} />}
                onClick={() => { setDetailTarget(null); openEdit(detailTarget); }}>
                Edit
              </Button>
            </Group>
            )}
          </Stack>
        )}
      </Drawer>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Medical Entry' : 'New Medical Entry'} centered size="lg">
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Survivor" required searchable
              data={survivorOptions} value={form.survivorId ?? ''}
              onChange={v => setField('survivorId', v ?? '')}
              placeholder="Select survivor" />
            <Select label="Entry Type" required data={ENTRY_TYPE_OPTIONS}
              value={form.entryType ?? 'note'}
              onChange={v => setField('entryType', v ?? 'note')} />
          </SimpleGrid>

          <Textarea label="Description" required value={form.description ?? ''}
            onChange={e => setField('description', e.currentTarget.value)}
            autosize minRows={2} />

          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <TextInput label="Medication Name" value={form.medicationName ?? ''}
              onChange={e => setField('medicationName', e.currentTarget.value)} />
            <TextInput label="Dosage" value={form.dosage ?? ''}
              onChange={e => setField('dosage', e.currentTarget.value)} />
            <TextInput label="Administered By" value={form.administeredBy ?? ''}
              onChange={e => setField('administeredBy', e.currentTarget.value)} />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Switch label="Follow-Up Needed" checked={!!form.followUpNeeded}
              onChange={e => setField('followUpNeeded', e.currentTarget.checked)} mt="md" />
            {form.followUpNeeded && (
              <TextInput label="Follow-Up Date & Time" type="datetime-local"
                value={(form.followUpDate || '').replace('Z', '').slice(0, 16)}
                onChange={e => setField('followUpDate', e.currentTarget.value)} />
            )}
          </SimpleGrid>

          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.currentTarget.value)} autosize minRows={2} />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Entry'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
